import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import type { L2CDriver, L2CMover, L2CPanel } from '../../../types/leadToCash';
import { BreakdownTableBodyShimmer } from './LeadToCashLoadingSkeleton';
import {
  formatL2CCurrency,
  formatL2CNumber,
  formatL2CPercent,
  formatL2CSignedCurrency,
  formatL2CSignedNumber,
} from '../utils/formatNumbers';

const MOVER_STATUS_COLORS: Record<string, string> = {
  Notable: '#ef4444',
  Watch: '#eab308',
  Normal: '#22c55e',
};

function moverStatusColor(status: string): string {
  return MOVER_STATUS_COLORS[status] ?? MOVER_STATUS_COLORS.Normal;
}

function HeaderWithTooltip({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        component="span"
        sx={{
          borderBottom: '1px dashed',
          borderColor: 'text.disabled',
          cursor: 'help',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

function TableToolbar() {
  return (
    <GridToolbarContainer
      sx={{
        px: 2,
        py: 1,
        gap: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        justifyContent: 'flex-end',
      }}
    >
      <Box sx={{ width: { xs: '100%', sm: 280 } }}>
        <GridToolbarQuickFilter debounceMs={250} />
      </Box>
    </GridToolbarContainer>
  );
}

export interface BreakdownMetricContext {
  label: string;
  formattedValue?: string | null;
  source: 'headline' | 'supporting';
  /** Metric is an average/rate (e.g. AVG(...) per dimension) — per-row values
   * are independent averages and will not sum to the headline total. */
  isAverage?: boolean;
  /** Metric is a currency amount — this_period/prior_period/change columns
   * should render with the tenant's currency symbol. */
  isCurrency?: boolean;
}

interface StageBreakdownTableProps {
  panel: NonNullable<L2CPanel>;
  drivers: L2CDriver[];
  activeDriver?: string | null;
  currentPeriodLabel: string;
  priorPeriodLabel: string;
  comparisonLabel: string;
  driverLoading?: boolean;
  breakdownMetric?: BreakdownMetricContext | null;
  /** Tenant currency symbol (e.g. "$", "€") used when breakdownMetric.isCurrency. */
  currencySymbol?: string;
  onDriverChange?: (driverId: string) => void;
}

export default function StageBreakdownTable({
  panel,
  drivers,
  activeDriver,
  currentPeriodLabel,
  priorPeriodLabel,
  comparisonLabel,
  driverLoading = false,
  breakdownMetric = null,
  currencySymbol = '$',
  onDriverChange,
}: StageBreakdownTableProps) {
  const theme = useTheme();

  const activeDriverId = activeDriver ?? drivers.find((d) => d.default)?.id ?? drivers[0]?.id ?? null;
  const isCurrency = Boolean(breakdownMetric?.isCurrency);

  // Prefer the full mover list stored per driver (client-side paginated),
  // falling back to the current page slice when the full store is absent.
  const allMovers: L2CMover[] = React.useMemo(() => {
    const store = activeDriverId ? panel.breakdowns?.[activeDriverId] : undefined;
    return store?.movers ?? panel.movers ?? [];
  }, [panel.breakdowns, panel.movers, activeDriverId]);

  const gridRows = React.useMemo(
    () =>
      allMovers.map((row, index) => ({
        id: `${row.dimension}-${index}`,
        dimension: row.dimension,
        this_period: row.this_period,
        prior_period: row.prior_period,
        change: row.change,
        change_pct: row.change_pct,
        abs_change: Math.abs(row.change),
        status: row.status,
      })),
    [allMovers],
  );

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'dimension',
        headerName: 'Dimension',
        minWidth: 160,
        flex: 1.4,
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label="Dimension" tooltip="The dimension value driving this stage's change." />
        ),
      },
      {
        field: 'this_period',
        headerName: currentPeriodLabel,
        type: 'number',
        minWidth: 104,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label={currentPeriodLabel} tooltip="Value for the selected period." />
        ),
        valueFormatter: (value: number | null) =>
          value === null || value === undefined
            ? '—'
            : isCurrency
              ? formatL2CCurrency(value, currencySymbol)
              : formatL2CNumber(value),
      },
      {
        field: 'prior_period',
        headerName: priorPeriodLabel,
        type: 'number',
        minWidth: 104,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label={priorPeriodLabel} tooltip="Value for the comparison period." />
        ),
        valueFormatter: (value: number | null) =>
          value === null || value === undefined
            ? '—'
            : isCurrency
              ? formatL2CCurrency(value, currencySymbol)
              : formatL2CNumber(value),
      },
      {
        field: 'change',
        headerName: 'Change',
        type: 'number',
        minWidth: 104,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label="Change" tooltip="Absolute change vs the comparison period." />
        ),
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value === null || params.value === undefined ? null : Number(params.value);
          if (value === null) return '—';
          const status = String(params.row.status);
          const notable = status === 'Notable' || status === 'Watch';
          const color = value === 0
            ? theme.palette.text.secondary
            : notable
              ? value < 0 ? theme.palette.error.main : theme.palette.success.main
              : theme.palette.text.primary;
          return (
            <Box component="span" sx={{ fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
              {isCurrency ? formatL2CSignedCurrency(value, currencySymbol) : formatL2CSignedNumber(value)}
            </Box>
          );
        },
      },
      {
        field: 'change_pct',
        headerName: `Change (${comparisonLabel})`,
        type: 'number',
        minWidth: 116,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label={`Change (${comparisonLabel})`}
            tooltip={`Percentage change vs the comparison period (${comparisonLabel}).`}
          />
        ),
        valueFormatter: (value: number | null) =>
          value === null || value === undefined ? '—' : formatL2CPercent(value, true),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 108,
        flex: 0.7,
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label="Status" tooltip="How notable this mover is vs the comparison period." />
        ),
        renderCell: (params: GridRenderCellParams) => {
          const status = String(params.value);
          const color = moverStatusColor(status);
          return (
            <Chip
              size="small"
              label={status}
              variant="outlined"
              sx={{ fontWeight: 600, borderColor: alpha(color, 0.4), color }}
            />
          );
        },
      },
      {
        field: 'abs_change',
        headerName: 'Abs change',
        type: 'number',
        sortable: true,
      },
    ],
    [currentPeriodLabel, priorPeriodLabel, comparisonLabel, theme.palette, isCurrency, currencySymbol],
  );

  const gridSx = React.useMemo(
    () => ({
      border: 0,
      borderRadius: 0,
      fontSize: '0.8125rem',
      '& .MuiDataGrid-columnHeaders': {
        bgcolor: theme.tokens?.surface?.sunken ?? 'action.hover',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '36px !important',
        maxHeight: '36px !important',
      },
      '& .MuiDataGrid-columnHeader': {
        '&:focus, &:focus-within': { outline: 'none' },
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 600,
        fontSize: '0.7rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      },
      '& .MuiDataGrid-cell': {
        borderColor: theme.palette.divider,
        '&:focus, &:focus-within': { outline: 'none' },
      },
      '& .MuiDataGrid-row:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.04),
      },
      '& .MuiDataGrid-footerContainer': {
        borderTop: `1px solid ${theme.palette.divider}`,
        minHeight: 52,
      },
      '& .MuiTablePagination-root': {
        fontSize: '0.75rem',
      },
    }),
    [theme],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        mb={1.5}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Dimension breakdown
          </Typography>
          {breakdownMetric && (
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap mt={0.5}>
              <Typography variant="body2" color="text.secondary">
                Showing breakdown for
              </Typography>
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {breakdownMetric.label}
              </Typography>
              {breakdownMetric.formattedValue && (
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  ({breakdownMetric.formattedValue} this period)
                </Typography>
              )}
              <Chip
                size="small"
                variant="outlined"
                label={breakdownMetric.source === 'supporting' ? 'Supporting metric' : 'Headline metric'}
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={breakdownMetric ? 0.5 : 0}>
            {panel.lens_hint || 'Sorted by largest absolute change vs the comparison period. Click a column to sort; use search to filter.'}
          </Typography>
          {breakdownMetric?.isAverage && (
            <Typography variant="caption" color="warning.main" display="block" mt={0.25}>
              This is a per-dimension average, not a total — rows won&apos;t sum to the headline value above.
            </Typography>
          )}
        </Box>
        {drivers.length > 1 && activeDriverId && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={activeDriverId}
            onChange={(_, value) => {
              if (value && value !== activeDriverId && onDriverChange) {
                onDriverChange(value);
              }
            }}
          >
            {drivers.map((d) => (
              <ToggleButton key={d.id} value={d.id} sx={{ textTransform: 'none', px: 1.5 }}>
                {d.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={(t) => ({
          overflow: 'hidden',
          borderRadius: `${t.ds?.radius?.md ?? 8}px`,
          border: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        {driverLoading ? (
          <BreakdownTableBodyShimmer rowCount={8} />
        ) : (
          <Box sx={{ width: '100%', minHeight: 360 }}>
            <DataGrid
              rows={gridRows}
              columns={columns}
              disableRowSelectionOnClick
              sortingMode="client"
              paginationMode="client"
              pageSizeOptions={[10, 25, 50]}
              localeText={{
                toolbarQuickFilterPlaceholder: 'Search dimensions…',
                noRowsLabel: 'No dimension movers for this period.',
              }}
              initialState={{
                sorting: { sortModel: [{ field: 'abs_change', sort: 'desc' }] },
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              columnVisibilityModel={{ abs_change: false }}
              slots={{ toolbar: TableToolbar }}
              sx={gridSx}
              autoHeight
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
